import { Button, buttonClass, cardClass, textareaClass } from '@ledgerloom/ui';
import { Fragment, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, type Outcome } from '../api';
import { useApi } from '../hooks';
import { useSession } from '../session';
import { ErrorBanner, Loading, outcomeMessage, outcomeTone, useToast } from '../components/ui';
import { Field } from '../components/Field';
import { Stepper } from '../components/Stepper';
import { EmbedCode } from '../components/EmbedCode';
import { areaDone, Wishes, type Interview, type SetupArea } from './Welcome';

// The walkthrough of the workspace's setup (docs/plans/owner-onboarding-plan.md,
// phase 6): one area at a time, on the screen the area changes, with its draft
// beside the current setting and three answers: Looks good signs it, Change
// something sends it back to the agent with a note, Skip for now sets it aside.
// Moving between steps writes nothing. Pattern: advanced/wizard.

// Each step: the screen it opens, the section there (its heading's id), what
// to do, and for a step a person takes, the answer that means "none here"
// (a Confirmed mark). A step no screen edits opens the welcome screen.
interface Step { screen: string; section?: string; todo: string; none?: string }
const STEPS: Record<string, Step> = {
  business: { screen: '/welcome', todo: 'Check what Mazu knows about the business below: what it sells, its industry, size, region, and currency. No screen changes these. If something is missing or wrong, press Change something and say what it should be: the onboarding agent drafts it for you to approve.' },
  you: { screen: '/welcome', todo: 'Check your name, role, and time zone below. Agents keep your schedule in your time zone. No screen changes these. If something is missing or wrong, press Change something and say what it should be: the onboarding agent drafts it for you to approve.' },
  signoff: { screen: '/admin/workspace', section: 'signoff-h', todo: 'Under Sign-off, tick the changes you may apply on your own. Leave it as it is if another admin signs your changes.' },
  people: { screen: '/admin/people', section: 'people-h', todo: 'Under People, add each person who works here with their name, email, and role. Each one gets an email to sign in.', none: 'Just me' },
  mail: { screen: '/admin', section: 'mail-h', todo: 'Under Mail and calendar, press Connect Gmail or Connect Microsoft 365 and sign in with your work account. Your mail and meetings then reach Mazu, and replies you send from it go out as you.' },
  integrations: { screen: '/admin/connections', section: 'conn-h', todo: 'Under Connectors, add the other tools agents may use: your website\'s repository, or a website an agent signs in to.', none: 'I use no other tools' },
  pipeline: { screen: '/pipeline', todo: 'Check the steps a deal moves through. A draft from the workspace designer shows under Proposed: approve it, or press Change something.' },
  leads: { screen: '/leads', todo: 'Check where leads come from, who takes them, and how they are scored. A draft from the workspace designer shows under Proposed: approve it, or press Change something.' },
  data: { screen: '/import', todo: 'Bring in your contacts and deals from where they live now: upload a file on this page, or hire the migration agent to move them for you.', none: 'Nothing to import' },
  knowledge: { screen: '/admin/business-model', todo: 'Check what agents know about the company below: what it sells, who buys, and what it never says. Press Change something to add or correct anything.' },
  agents: { screen: '/agents/hire', todo: 'Hire the agents the setup recommends, shown first on this page, or describe the job to find another. Each starts on trial, so you see what it would do before it acts.' },
};
// You: a note to the agent starts with the time zone this browser is in, when Mazu has none.
const startNote = (a: SetupArea) => {
  const person = a.now.person as { timezone?: string | null } | undefined;
  return a.key === 'you' && person && !person.timezone ? `My time zone is ${Intl.DateTimeFormat().resolvedOptions().timeZone}. My role is ` : '';
};
const stepHref = (key: string) => `${STEPS[key]?.screen ?? '/welcome'}?setup=${key}`;

// Brings the step's section into view, focused and outlined. The screen loads
// its data after the panel, so look for the heading for a few seconds.
function useSection(id: string | undefined) {
  useEffect(() => {
    if (!id) return;
    let target: HTMLElement | null = null;
    let tries = 0;
    const t = setInterval(() => {
      const heading = document.getElementById(id);
      if (!heading && ++tries < 30) return;
      clearInterval(t);
      if (!heading) return;
      target = heading.closest('section') ?? heading;
      target.classList.add('setup-target');
      heading.tabIndex = -1;
      heading.focus({ preventScroll: true });
      target.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }, 100);
    return () => { clearInterval(t); target?.classList.remove('setup-target'); };
  }, [id]);
}

const LABELS: Record<string, string> = {
  name: 'Name', profile: 'About the business', industry: 'Industry', size_band: 'People', region_band: 'Where', product_mix: 'What it sells', currency: 'Currency',
  person: 'You', display_name: 'Name', title: 'Role', timezone: 'Time zone', solo_signoff: 'You approve changes alone', approvers: 'People who approve',
  people: 'People', connected: 'Connected', stages: 'Steps', routing_rules: 'Routing rules', forms: 'Forms', score_model: 'Score model version',
  just_me: 'Nobody else needs a login', no_other_tools: 'No other tools to connect', nothing_to_import: 'Nothing to import',
  imports: 'Imports done', known: 'What agents know', hired: 'Agents hired', recommended_agents: 'Agents to hire', wishes: 'Also asked for', rules: 'Rules', fields: 'Fields',
};
const label = (k: string) => LABELS[k] ?? k.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
// What a list item is called, in the words a person reads: a stage's name, a person and their email, a claim.
const LINE = ['name', 'display_name', 'email', 'role', 'claim', 'provider', 'connector', 'template_id', 'why', 'wish'];
const line = (o: Record<string, unknown>) => LINE.filter((k) => o[k] != null && o[k] !== '').map((k) => String(o[k])).join(' · ');

// A setting or a draft as a short list of facts. Ids and the reason are left out: the reason shows on its own.
// An empty field shows as Not set, so the step shows what is missing.
function Facts({ value: raw }: { value: Record<string, unknown> }) {
  // One setting wrapped in one object (You's person) reads as its own fields.
  const only = Object.values(raw).length === 1 ? Object.values(raw)[0] : null;
  const value = only && typeof only === 'object' && !Array.isArray(only) ? only as Record<string, unknown> : raw;
  const shown = Object.entries(value).filter(([k, v]) => k !== 'reason' && !k.endsWith('_id') && !(Array.isArray(v) && !v.length));
  if (!shown.length) return <p className="muted small" style={{ margin: 0 }}>Nothing set yet.</p>;
  const show = (v: unknown): ReactNode => {
    if (v == null || v === '') return <span className="muted">Not set</span>;
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    if (Array.isArray(v)) return <ul style={{ margin: 0, paddingLeft: 18 }}>{v.map((x, i) => <li key={i}>{typeof x === 'object' && x ? line(x as Record<string, unknown>) : String(x)}</li>)}</ul>;
    if (typeof v === 'object' && v) return <Facts value={v as Record<string, unknown>} />;
    return String(v);
  };
  return <dl className="facts">{shown.map(([k, v]) => <Fragment key={k}><dt>{label(k)}</dt><dd>{show(v)}</dd></Fragment>)}</dl>;
}

export function SetupPanel({ areaKey }: { areaKey: string }) {
  const iv = useApi<Interview>('/v1/onboarding');
  // The Leads step hands out each form's embed code.
  const forms = useApi<Array<{ id: string; name: string; status: string; embed: string }>>(areaKey === 'leads' ? '/v1/forms' : null);
  const navigate = useNavigate();
  const toast = useToast();
  const { me } = useSession();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [changing, setChanging] = useState(false);
  const [note, setNote] = useState('');
  const [waiting, setWaiting] = useState<string[] | null>(null);
  const reload = iv.reload;
  const step = STEPS[areaKey];
  useSection(step?.section);

  const areas = iv.data?.areas ?? [];
  const area = areas.find((a) => a.key === areaKey);
  // The steps still to do, in the fixed order; the one open stays in the list once done.
  const steps = areas.filter((a) => !areaDone(a) || a.key === areaKey);
  const at = steps.findIndex((a) => a.key === areaKey);
  const next = steps.slice(at + 1)[0];
  const prev = at > 0 ? steps[at - 1] : undefined;

  // After Change something, look again every two seconds until the agent's new draft arrives.
  const revised = !!waiting && !!area?.drafts.some((d) => !waiting.includes(d.proposal_id));
  useEffect(() => {
    if (!waiting) return;
    if (revised) { setWaiting(null); return; }
    const t = setInterval(() => void reload(), 2000);
    return () => clearInterval(t);
  }, [waiting, revised, reload]);
  useEffect(() => { setChanging(false); setNote(''); setWaiting(null); setError(null); }, [areaKey]);

  async function run(key: string, fn: () => Promise<unknown>) {
    setBusy(key); setError(null);
    try { await fn(); await reload(); } catch (e) { setError(e); } finally { setBusy(null); }
  }
  const go = (a: SetupArea | undefined) => navigate(a ? stepHref(a.key) : '/welcome');

  if (iv.loading && !iv.data) return <section className={`${cardClass} card-pad setup-panel`} aria-label="Workspace setup"><Loading label="Loading the setup" /></section>;
  if (!area) {
    return (
      <section className={`${cardClass} card-pad stack setup-panel`} aria-label="Workspace setup">
        <ErrorBanner error={iv.error} />
        <p style={{ margin: 0 }}>This workspace has no such setup step.</p>
        <Link className={buttonClass({ variant: 'secondary' })} to="/welcome">Back to the setup</Link>
      </section>
    );
  }

  const looksGood = () => run('good', async () => {
    if (!area.drafts.length) { await api('/v1/onboarding', { body: { action: 'mark', area: area.key, mark: 'confirmed' } }); toast(`${area.title}: confirmed`); return; }
    for (const d of area.drafts) {
      if (!d.approval_id) continue;
      const out = await api<Outcome>(`/v1/approvals/${d.approval_id}/decide`, { body: { decision: 'approve' } });
      toast(outcomeMessage(out, `${area.title}: approved`), outcomeTone(out));
    }
  });
  const skip = () => run('skip', async () => {
    await api('/v1/onboarding', { body: { action: 'mark', area: area.key, mark: 'skipped' } });
    go(next);
  });
  async function sendChange(e: FormEvent) {
    e.preventDefault();
    if (!note.trim()) return;
    const before = area!.drafts.map((d) => d.proposal_id);
    await run('change', async () => {
      await api('/v1/onboarding', { body: { action: 'change', area: area!.key, note: note.trim() } });
      setWaiting(before); setChanging(false); setNote('');
    });
  }
  const hire = (templateId: string) => run(`hire-${templateId}`, async () => {
    const out = await api<Outcome>('/v1/agents/hire', { body: { template_id: templateId, reason: 'Hired from the workspace setup', principal_type: 'tenant' } });
    toast(outcomeMessage(out, 'Hired, on trial'), outcomeTone(out));
  });

  const done = areaDone(area);
  const recommended = (iv.data?.setup?.payload.recommended_agents ?? []) as Array<{ template_id: string; why: string }>;
  const wishes = iv.data?.setup?.payload.wishes ?? [];
  // The person's answers for this step, newest last; their text is the person's, shown as text.
  const told = (iv.data?.rounds ?? []).flatMap((r) => r.questions).filter((q) => q.area === area.key && q.answer);
  // A step only the person takes (connect, add a connector, import) has no agent to tell what to change.
  const own = area.by === 'person' && !area.drafts.length;
  // Without a draft, its own none-here answer confirms it; a step only the person takes and has none for offers only Skip.
  const confirmLabel = !area.drafts.length && step?.none ? step.none : own ? null : 'Looks good';

  return (
    <section className={`${cardClass} card-pad stack setup-panel`} aria-labelledby="setup-step-h">
      <p className="faint small" style={{ margin: 0 }}>Workspace setup · step {at + 1} of {steps.length}</p>
      <Stepper steps={steps.map((s) => s.title)} current={at} />
      <h2 id="setup-step-h" style={{ margin: 0 }}>{area.title}</h2>
      {step && !done && <p style={{ margin: 0 }}>{step.todo}</p>}
      <ErrorBanner error={error ?? iv.error} focusOnShow />
      {waiting && !revised ? (
        <div role="status" aria-live="polite" className="row" style={{ gap: 12 }}><div className="spinner" aria-hidden="true" /><span>The agent is drafting it again from your note…</span></div>
      ) : done ? (
        <p role="status" style={{ margin: 0 }}>{area.state === 'confirmed' ? 'You said this is right.' : 'This is set up.'}</p>
      ) : null}

      {area.drafts.map((d) => (
        <div key={d.proposal_id} className="stack" style={{ gap: 8 }}>
          <h3 style={{ margin: 0, fontSize: 15 }}>Proposed</h3>
          <Facts value={d.payload} />
          {typeof d.payload.reason === 'string' && <p className="muted small" style={{ margin: 0 }}>{d.payload.reason}</p>}
        </div>
      ))}
      {!done && told.length > 0 && (
        <div className="stack" style={{ gap: 8 }}>
          <h3 style={{ margin: 0, fontSize: 15 }}>You told the agent</h3>
          <dl className="facts">{told.map((q, i) => <Fragment key={i}><dt>{q.text}</dt><dd style={{ whiteSpace: 'pre-wrap' }}>{q.answer}</dd></Fragment>)}</dl>
        </div>
      )}
      <div className="stack" style={{ gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>In Mazu now</h3>
        <Facts value={area.now} />
      </div>

      {!done && !area.drafts.length && (
        <div className="row">
          {area.key === 'data' && <Button type="button" variant="secondary" disabled={!!busy} onClick={() => void hire('migration-agent')}>Hire the migration agent</Button>}
          {area.key === 'agents' && recommended.map((r) => (
            <Button key={r.template_id} type="button" variant="secondary" disabled={!!busy} onClick={() => void hire(r.template_id)}>Hire {r.template_id.replace(/-/g, ' ')}</Button>
          ))}
          {area.key === 'agents' && !recommended.length && !wishes.length && <Link className={buttonClass({ variant: 'secondary' })} to="/agents/hire">See the agents you can hire</Link>}
        </div>
      )}
      {area.key === 'leads' && forms.data?.some((f) => f.status === 'active') && (
        <div className="stack" style={{ gap: 8 }}>
          <h3 style={{ margin: 0, fontSize: 15 }}>Put a form on your site</h3>
          {forms.data.filter((f) => f.status === 'active').map((f) => <EmbedCode key={f.id} name={f.name} code={f.embed} />)}
        </div>
      )}
      {area.key === 'agents' && iv.data?.setup?.payload.wishes?.length ? (
        <div className="stack" style={{ gap: 8 }}>
          <h3 style={{ margin: 0, fontSize: 15 }}>You also asked for</h3>
          <Wishes interview={iv.data} />
        </div>
      ) : null}

      {!done && !(waiting && !revised) && (
        changing ? (
          <form className="stack" onSubmit={sendChange} style={{ gap: 8 }}>
            <Field label="What should change?" hint="The agent that drafted it reads your note and drafts it again.">
              <textarea className={textareaClass} rows={3} value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} autoFocus />
            </Field>
            <div className="row">
              <Button type="submit" disabled={!!busy || !note.trim()}>{busy === 'change' ? 'Sending…' : 'Send'}</Button>
              <Button type="button" variant="ghost" onClick={() => setChanging(false)}>Cancel</Button>
            </div>
          </form>
        ) : (
          <div className="row">
            {confirmLabel && <Button type="button" disabled={!!busy} onClick={() => void looksGood()}>{busy === 'good' ? 'Saving…' : confirmLabel}</Button>}
            {!own && <Button type="button" variant="secondary" disabled={!!busy} onClick={() => { setChanging(true); setNote(startNote(area)); }}>Change something</Button>}
            <Button type="button" variant="ghost" disabled={!!busy} onClick={() => void skip()}>Skip for now</Button>
          </div>
        )
      )}

      <nav className="row" aria-label="Setup steps" style={{ justifyContent: 'space-between' }}>
        {prev ? <Link to={stepHref(prev.key)}>Back: {prev.title}</Link> : <span />}
        {next ? <Link className={buttonClass({ variant: 'secondary', size: 'sm' })} to={stepHref(next.key)}>Next: {next.title}</Link> : <Link className={buttonClass({ variant: 'secondary', size: 'sm' })} to="/welcome">Finish</Link>}
      </nav>
      {me.role !== 'owner' && <p className="small muted" style={{ margin: 0 }}>Only the workspace owner signs the setup.</p>}
    </section>
  );
}

// /welcome/setup: the first step still to do, on its screen.
export function SetupStart() {
  const iv = useApi<Interview>('/v1/onboarding');
  const navigate = useNavigate();
  const first = iv.data?.areas.find((a) => !areaDone(a));
  useEffect(() => { if (iv.data) navigate(first ? stepHref(first.key) : '/welcome', { replace: true }); }, [iv.data, first, navigate]);
  return iv.error ? <ErrorBanner error={iv.error} /> : <Loading label="Opening the setup" />;
}
